import React, {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {
  Search,
  Plus,
  Filter,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Receipt,
  MoreHorizontal,
  Edit,
  Trash2,
  Loader2,
} from 'lucide-react'
import {getAccountExpenses, deleteAccountExpense} from '../core/_requests'
import {useAuth} from '../../auth'
import {ACCOUNTS_PERMS} from '../core/permissions'
import {isCustomAccountType} from '../core/expenseAccountTypes'
import {
  AccountsActionDropdown,
  DropdownPosition,
  getDropdownPosition,
} from './AccountsActionDropdown'

export default function AccountsPurchaseExpenses() {
  const navigate = useNavigate()
  const {hasPermission} = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilter, setActiveFilter] = useState('All')
  const filters = ['All', 'This Month', 'Older']

  const [expenses, setExpenses] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null)
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null)

  const fetchExpenses = async () => {
    setIsLoading(true)
    try {
      const data = await getAccountExpenses()
      setExpenses(data || [])
    } catch (err) {
      console.error('Failed to fetch expenses', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchExpenses()
  }, [])

  useEffect(() => {
    const closeDropdowns = () => {
      setActiveDropdown(null)
      setDropdownPos(null)
    }
    document.addEventListener('click', closeDropdowns)
    return () => document.removeEventListener('click', closeDropdowns)
  }, [])

  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null)
    setDropdownPos(null)
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    if (!window.confirm('Are you sure you want to delete this expense?')) return
    try {
      await deleteAccountExpense(id)
      fetchExpenses()
    } catch (err) {
      console.error('Failed to delete', err)
      alert('Failed to delete expense')
    }
  }

  const filteredExpenses = expenses.filter((e) => {
    if (activeFilter === 'This Month') {
      const expenseDate = new Date(e.expenseDate || e.expense_date)
      const now = new Date()
      if (
        expenseDate.getMonth() !== now.getMonth() ||
        expenseDate.getFullYear() !== now.getFullYear()
      )
        return false
    }

    if (searchQuery) {
      const sq = searchQuery.toLowerCase()
      if (
        !e.account?.toLowerCase().includes(sq) &&
        !(e.vendorName || e.vendor_name)?.toLowerCase().includes(sq) &&
        !e.referenceNumber?.toLowerCase().includes(sq) &&
        !e.vesselName?.toLowerCase().includes(sq) &&
        !e.accountCode?.toLowerCase().includes(sq) &&
        !e.account_code?.toLowerCase().includes(sq)
      ) {
        return false
      }
    }
    return true
  })

  const safeNumber = (val: any): number => {
    let num = Number(val)
    if (isNaN(num)) {
      const clean = typeof val === 'string' ? val.replace(/[^0-9.-]/g, '') : ''
      num = Number(clean)
      if (isNaN(num)) num = 0
    }
    return num
  }

  const handleDownloadCSV = () => {
    if (filteredExpenses.length === 0) {
      alert('No expense data to export.')
      return
    }
    const formatCurrency = (val: any) => {
      return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(
        safeNumber(val)
      )
    }
    const headers = [
      'Sr. No.',
      'Date',
      'Expense Account',
      'Vessel',
      'Account Code',
      'Reference#',
      'Vendor',
      'Paid Through',
      'Base Amount',
      'GST / Tax Name',
      'GST / Tax Amount',
      'Final Expense Total',
    ]
    const rows = filteredExpenses.map((exp, index) => {
      const baseAmt = safeNumber(exp.amount)
      const taxAmt = safeNumber(exp.taxAmount)
      const total = baseAmt + taxAmt
      const expDate = exp.expenseDate || exp.expense_date || ''
      return [
        index + 1,
        expDate ? new Date(expDate).toLocaleDateString() : '-',
        exp.account || '-',
        exp.vesselName || exp.vessel_name || '-',
        exp.accountCode || exp.account_code || '-',
        exp.referenceNumber || '-',
        exp.vendorName || '-',
        exp.paidThrough || '-',
        formatCurrency(baseAmt),
        exp.taxName || (taxAmt > 0 ? 'GST' : 'None'),
        formatCurrency(taxAmt),
        formatCurrency(total),
      ]
    })

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(',')),
    ].join('\n')

    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', `expenses_export_${new Date().toISOString().slice(0, 10)}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const totalExpensesAmount = expenses.reduce(
    (sum, e) => sum + safeNumber(e.amount) + safeNumber(e.taxAmount),
    0
  )
  const totalGstAmount = expenses.reduce(
    (sum, e) => sum + safeNumber(e.taxAmount),
    0
  )
  const thisMonthExpenses = expenses
    .filter((e) => {
      const d = new Date(e.expenseDate || e.expense_date)
      const now = new Date()
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
    })
    .reduce((sum, e) => sum + safeNumber(e.amount) + safeNumber(e.taxAmount), 0)

  const formatCurrency = (val: any) => {
    return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(
      safeNumber(val)
    )
  }

  const stats = [
    {
      label: 'Total Expenses',
      value: formatCurrency(totalExpensesAmount),
      change: '+0%',
      trend: 'up',
      color: 'blue',
    },
    {
      label: 'This Month',
      value: formatCurrency(thisMonthExpenses),
      change: '+0%',
      trend: 'up',
      color: 'emerald',
    },
    {
      label: 'Total GST / Tax',
      value: formatCurrency(totalGstAmount),
      change: '+0%',
      trend: 'up',
      color: 'indigo',
    },
    {label: 'Count', value: expenses.length.toString(), change: '0%', trend: 'up', color: 'violet'},
  ]

  const exp =
    activeDropdown !== null ? filteredExpenses.find((exp) => exp.id === activeDropdown) : null

  return (
    <>
      <div className='flex h-screen w-full overflow-hidden bg-slate-50'>
        <div id='sidebar-overlay' className='fixed inset-0 bg-black/50 z-40 hidden md:hidden'></div>
        <main className='flex-1 flex flex-col h-full overflow-hidden relative'>
          <div
            className='flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 space-y-5'
            id='main-scroll'
          >
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
              <div>
                <div className='text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1'>
                  Accounts / Purchase
                </div>
                <h1 className='text-xl font-bold text-slate-900 leading-tight'>Expenses</h1>
                <p className='text-xs text-slate-500'>Track and categorize all business expenses</p>
              </div>
              <div className='flex items-center gap-2'>
                <div className='flex items-center gap-2 text-[11px] text-slate-500'>
                  <span>Data refreshed just now</span>
                  <button
                    onClick={fetchExpenses}
                    className='p-1 rounded hover:bg-slate-100 transition-colors'
                  >
                    <RefreshCw
                      className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin text-blue-500' : ''}`}
                    />
                  </button>
                </div>
                {hasPermission(ACCOUNTS_PERMS.EXPENSE_CREATE) && (
                  <button
                    onClick={() => navigate('/accounts/purchase/expenses/new')}
                    className='px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2'
                  >
                    <Plus className='w-4 h-4' />
                    New Expense
                  </button>
                )}
              </div>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4'>
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className='card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm'
                >
                  <div className='flex items-center justify-between mb-3'>
                    <div
                      className={`w-9 h-9 rounded-xl bg-${stat.color}-50 flex items-center justify-center`}
                    >
                      <Receipt className={`w-4.5 h-4.5 text-${stat.color}-600`} />
                    </div>
                    <span
                      className={`text-[10px] font-semibold flex items-center gap-0.5 ${
                        stat.trend === 'up' ? 'text-emerald-600' : 'text-red-500'
                      }`}
                    >
                      {stat.trend === 'up' ? (
                        <ArrowUpRight className='w-3 h-3' />
                      ) : (
                        <ArrowDownRight className='w-3 h-3' />
                      )}
                      {stat.change}
                    </span>
                  </div>
                  <div className='text-2xl font-bold text-slate-900'>{stat.value}</div>
                  <div className='text-[11px] text-slate-500 mt-0.5'>{stat.label}</div>
                </div>
              ))}
            </div>

            <div className='bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden'>
              <div className='p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
                <div className='flex items-center gap-2 overflow-x-auto scrollbar-hide'>
                  {filters.map((f) => (
                    <button
                      key={f}
                      onClick={() => setActiveFilter(f)}
                      className={`px-3 py-1.5 text-[11px] font-bold rounded-lg whitespace-nowrap transition-all ${
                        activeFilter === f
                          ? 'bg-slate-900 text-white'
                          : 'text-slate-500 hover:bg-slate-100'
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                <div className='flex items-center gap-2'>
                  <div className='relative'>
                    <Search className='w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400' />
                    <input
                      type='text'
                      placeholder='Search expenses...'
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className='pl-9 pr-4 py-2 w-56 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all'
                    />
                  </div>
                  {/* Filter button kept for future use.
                  <button className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"><Filter className="w-4 h-4 text-slate-500" /></button>
                  */}
                  <button
                    onClick={handleDownloadCSV}
                    className='p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors'
                  >
                    <Download className='w-4 h-4 text-slate-500' />
                  </button>
                </div>
              </div>

              <div className='overflow-x-auto pb-24 min-h-[300px]'>
                <table className='w-full text-left min-w-175'>
                  <thead>
                    <tr className='text-[10px] text-slate-400 uppercase tracking-wider bg-slate-50/50'>
                      <th className='px-4 py-3 font-medium text-center text-slate-400'>Sr. No.</th>
                      <th className='px-4 py-3 font-medium'>Date</th>
                      <th className='px-4 py-3 font-medium'>Expense Account</th>
                      <th className='px-4 py-3 font-medium'>Vessel</th>
                      <th className='px-4 py-3 font-medium'>Account Code</th>
                      <th className='px-4 py-3 font-medium'>Reference#</th>
                      <th className='px-4 py-3 font-medium'>Vendor</th>
                      <th className='px-4 py-3 font-medium'>Paid Through</th>
                      <th className='px-4 py-3 font-medium text-right'>Base Amount</th>
                      <th className='px-4 py-3 font-medium text-right'>GST / Tax</th>
                      <th className='px-4 py-3 font-medium text-right'>Final Total</th>
                      <th className='px-4 py-3 font-medium'></th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-slate-100 text-sm'>
                    {isLoading ? (
                      <tr>
                        <td colSpan={12} className='p-16 text-center'>
                          <Loader2 className='w-8 h-8 text-blue-500 animate-spin mx-auto mb-4' />
                          <p className='text-slate-500 font-medium'>Loading expenses...</p>
                        </td>
                      </tr>
                    ) : filteredExpenses.length === 0 ? (
                      <tr>
                        <td colSpan={12}>
                          <div className='p-16 flex flex-col items-center justify-center'>
                            <div className='w-20 h-20 bg-linear-to-br from-blue-50 to-blue-100 text-blue-500 rounded-2xl flex items-center justify-center mb-5'>
                              <Receipt className='w-10 h-10' strokeWidth={1.5} />
                            </div>
                            <h3 className='text-lg font-bold text-slate-900 mb-2'>
                              {searchQuery ? 'No matching expenses' : 'No expenses recorded'}
                            </h3>
                            <p className='text-slate-500 text-sm text-center max-w-sm mb-6'>
                              Record expenses to stay on top of your spending and tax deductions.
                            </p>
                            {!searchQuery && hasPermission(ACCOUNTS_PERMS.EXPENSE_CREATE) && (
                              <button
                                onClick={() => navigate('/accounts/purchase/expenses/new')}
                                className='px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2'
                              >
                                <Plus className='w-4 h-4' />
                                Record Expense
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredExpenses.map((exp, index) => {
                        const baseAmt = safeNumber(exp.amount)
                        const taxAmt = safeNumber(exp.taxAmount)
                        const total = baseAmt + taxAmt
                        const expDate = exp.expenseDate || exp.expense_date || ''
                        return (
                          <tr
                            key={exp.id}
                            className='hover:bg-slate-50 transition-colors group cursor-pointer'
                            onClick={() =>
                              hasPermission(ACCOUNTS_PERMS.EXPENSE_UPDATE) &&
                              navigate(`/accounts/purchase/expenses/${exp.id}`)
                            }
                          >
                            <td
                              className='px-4 py-3 text-slate-500 font-medium text-center'
                              onClick={(e) => e.stopPropagation()}
                            >
                              {index + 1}
                            </td>
                            <td className='px-4 py-3 font-medium text-slate-700'>
                              {expDate ? new Date(expDate).toLocaleDateString() : '-'}
                            </td>
                            <td className='px-4 py-3'>
                              <div className='flex items-center gap-1.5 flex-wrap'>
                                <span className='font-semibold text-blue-600 hover:underline'>
                                  {exp.account || '-'}
                                </span>
                                {isCustomAccountType(exp.account) && (
                                  <span className='px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200/80' title='Custom Account Type'>
                                    Custom
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className='px-4 py-3 text-slate-700 font-medium'>
                              {exp.vesselName || exp.vessel_name || <span className='text-slate-300'>-</span>}
                            </td>
                            <td className='px-4 py-3'>
                              {(exp.accountCode || exp.account_code) ? (
                                <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60 font-mono'>
                                  {exp.accountCode || exp.account_code}
                                </span>
                              ) : (
                                <span className='text-slate-300'>-</span>
                              )}
                            </td>
                            <td className='px-4 py-3 text-slate-500'>
                              {exp.referenceNumber || '-'}
                            </td>
                            <td className='px-4 py-3 text-slate-700 font-medium'>
                              {exp.vendorName || exp.vendor_name || '-'}
                            </td>
                            <td className='px-4 py-3 text-slate-500'>{exp.paidThrough || '-'}</td>
                            <td className='px-4 py-3 text-slate-700 font-mono text-right'>
                              {formatCurrency(baseAmt)}
                            </td>
                            <td className='px-4 py-3 text-right font-mono'>
                              {taxAmt > 0 ? (
                                <div className='flex flex-col items-end'>
                                  <span className='text-emerald-600 font-semibold'>+{formatCurrency(taxAmt)}</span>
                                  {exp.taxName && <span className='text-[10px] text-slate-400'>{exp.taxName}</span>}
                                </div>
                              ) : (
                                <span className='text-slate-300 text-xs'>$0.00</span>
                              )}
                            </td>
                            <td className='px-4 py-3 font-bold text-slate-900 text-right font-mono'>
                              {formatCurrency(total)}
                            </td>
                            <td className='px-4 py-3 text-right relative'>
                              <button
                                className='p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all focus:opacity-100'
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (activeDropdown === exp.id) {
                                    setActiveDropdown(null)
                                    setDropdownPos(null)
                                  } else {
                                    setDropdownPos(
                                      getDropdownPosition(e.currentTarget as HTMLElement)
                                    )
                                    setActiveDropdown(exp.id)
                                  }
                                }}
                              >
                                <MoreHorizontal className='w-4 h-4' />
                              </button>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>

      {exp && (
        <AccountsActionDropdown open={true} position={dropdownPos}>
          {hasPermission(ACCOUNTS_PERMS.EXPENSE_UPDATE) && (
            <button
              className='w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2'
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/accounts/purchase/expenses/${exp.id}`)
              }}
            >
              <Edit className='w-4 h-4 text-slate-400' /> Edit Expense
            </button>
          )}
          {hasPermission(ACCOUNTS_PERMS.EXPENSE_DELETE) && (
            <>
              <div className='h-px bg-slate-100 my-1'></div>
              <button
                className='w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2'
                onClick={(e) => handleDelete(e, exp.id)}
              >
                <Trash2 className='w-4 h-4 text-red-400' /> Delete Expense
              </button>
            </>
          )}
        </AccountsActionDropdown>
      )}
    </>
  )
}
